import { useCallback, useEffect, useMemo, useRef } from 'react';
import { OnboardingBaseTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { useOnboardingShallowStore, useRegisterOnboardingFlow } from '@frontend/product-onboarding';
import { useEligibility, usePreference } from '@frontend/user-preferences';
import { useMatch } from '@frontend/weave-router';
import { CI_BASE_CHECKLIST_POLICY, CI_BASE_FEATURE_IDS, CALL_HISTORY_PATH } from '../constant';
import { useBaseOnboardingProgress } from '../hooks/use-base-onboarding-progress';
import { useOnboardingEligibility } from '../hooks/use-onboarding-eligibility';
import { CI_BASE_ONBOARDING_EVENTS, trackBaseOnboardingEvent } from '../tracking';

/**
 * Registers the CI Base onboarding flow.
 * Mount once in the app shell.
 *
 * Important:
 * - This makes the Get Started checklist accessible globally.
 * - It does NOT auto-open the checklist.
 */
export function CIBaseOnboarding() {
  const isOnCallHistoryPage = useMatch({ from: CALL_HISTORY_PATH, shouldThrow: false });

  const { isEligible, isLoading } = useOnboardingEligibility();

  const { completedGroups, completeTask, flow } = useBaseOnboardingProgress({
    enabled: isEligible,
    trackLifecycle: isEligible && !isLoading,
  });

  const { isGuideOpen, toggleOnboardingGuide } = useOnboardingShallowStore('isGuideOpen', 'toggleOnboardingGuide');

  const [nudgeLifecycle] = usePreference('CIBaseChecklistNudgeLifecycle');

  const nudgeHandled = useRef(false);

  const hasInitialChecklistAutoOpenHappened = !!nudgeLifecycle.initialAutoOpenedAt;

  const { shouldShow: shouldNudge, recordDismiss } = useEligibility({
    featureId: CI_BASE_FEATURE_IDS.checklistNudge,
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: CI_BASE_CHECKLIST_POLICY.maxNudges,
      cooldownDays: CI_BASE_CHECKLIST_POLICY.nudgeCooldownDays,
    },
  });

  const isChecklistCompleted = useMemo(() => {
    return flow.tasks.every((task) => completedGroups.includes(task.id as OnboardingBaseTask));
  }, [flow.tasks, completedGroups]);

  const openOnboardingGuide = useCallback(() => {
    if (!isGuideOpen) {
      toggleOnboardingGuide();
    }
  }, [isGuideOpen, toggleOnboardingGuide]);

  useRegisterOnboardingFlow({
    flow,
    enabled: isEligible,
    isLoading,
    completedGroups,
    onGroupComplete: completeTask,
  });

  useEffect(() => {
    if (isLoading || !isEligible) return;
    /**
     * The checklist can be manually opened from anywhere,
     * but automatic nudge opens should only happen on Call History.
     */
    if (!isOnCallHistoryPage) return;

    /**
     * Nudges should start only after the checklist first auto-opens from:
     * - announcement banner click
     * - announcement banner view-limit expiry
     */
    if (!hasInitialChecklistAutoOpenHappened) return;

    /**
     * Prevent duplicate nudge opens during the same component lifecycle.
     */
    if (nudgeHandled.current) return;

    /**
     * Respect max nudge count and cooldown.
     */
    if (!shouldNudge) return;

    /**
     * Do not nudge once checklist is complete.
     */
    if (isChecklistCompleted) return;

    nudgeHandled.current = true;
    recordDismiss();
    trackBaseOnboardingEvent(CI_BASE_ONBOARDING_EVENTS.checklistNudgeOpened);
    openOnboardingGuide();
  }, [
    isLoading,
    isEligible,
    shouldNudge,
    completedGroups,
    flow,
    recordDismiss,
    openOnboardingGuide,
    isOnCallHistoryPage,
    hasInitialChecklistAutoOpenHappened,
    isChecklistCompleted,
  ]);

  return null;
}
